import dayjs from '../../utils/dayjs';
import {
  Badge,
  BlockStack,
  Box,
  Card,
  DataTable,
  DescriptionList,
  EmptyState,
  Icon,
  InlineGrid,
  InlineStack,
  Layout,
  Link,
  Page,
  Text,
} from '@shopify/polaris';
import SkeletonLoading from 'components/skeletonPageLoading';

import emptyIMG from 'media/images/empty.png';
import { helper_fmtNumber, helper_fmtTime, useGetChargeTransactions, useGetVehicle, VehicleStatus } from 'queries/charge_station.query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Player } from '@lottiefiles/react-lottie-player';
import evCharging from 'media/lottie_files/ev-charging.json';
import VehicleModal from './vehicle_modal';
import Pagination from 'components/pagination';
import { ChartLineIcon, ClockIcon, OrderIcon } from '@shopify/polaris-icons';

export default function VehicleView() {
  let { vehicle_id } = useParams();
  const history = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionQuery, setSessionQuery] = useState({
    page: 1,
    limit: 50,
    sort: 'startedAt:desc',
  });

  const vehicleIdNumber = useMemo(() => {
    if (!vehicle_id) return null;
    const parsed = Number(vehicle_id);
    return Number.isNaN(parsed) ? null : parsed;
  }, [vehicle_id]);

  const chargeTransactionQuery = useMemo(
    () => ({
      vehicleId: vehicleIdNumber,
      ...sessionQuery,
    }),
    [vehicleIdNumber, sessionQuery]
  );

  const { mutateAsync: getVehicleData, data: vehicleData, isPending } = useGetVehicle();
  const { data: sessionData, isLoading: isLoadingSessions } = useGetChargeTransactions(chargeTransactionQuery, vehicleIdNumber !== null);
  const vehicle = vehicleData?.vehicle_data;
  const todayAnalytics = vehicleData?.analytics;

  const fetchData = useCallback(
    async (vehicle_id: any) => {
      await getVehicleData(vehicle_id);
    },
    [getVehicleData]
  );

  useEffect(() => {
    if (vehicle_id) {
      setSessionQuery((currentQuery) => ({ ...currentQuery, page: 1 }));
      fetchData(vehicle_id);
    }
  }, [vehicle_id, fetchData]);

  const onChangeSessionPagination = useCallback((page: number, limit: number) => {
    setSessionQuery((currentQuery) => ({ ...currentQuery, page, limit }));
  }, []);

  const sessionRows = useMemo(() => {
    const sessions = sessionData?.entities ?? [];
    return [...sessions]
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
      .map((session) => [
        <Link
          onClick={() => {
            history(`/charge_transaction/view/${session.id}`);
          }}
        >
          {session.ocppTransactionId ?? '-'}
        </Link>,
        session.metrics?.socStart + ' -> ' + session.metrics?.socEnd,
        session.metrics?.energyKwh ?? '-',
        helper_fmtTime(session.metrics?.durationSeconds ?? 0),
        session.startedAt ? dayjs(new Date(session.startedAt)).format('HH:mm:ss DD-MM-YYYY') : '-',
        session.stoppedAt ? dayjs(new Date(session.stoppedAt)).format('HH:mm:ss DD-MM-YYYY') : '-',
        <Badge tone={session.status === 'STOPPED' ? 'success' : 'info'}>{session.status === 'STOPPED' ? 'Đã dừng' : 'Đang sạc'}</Badge>,
      ]);
  }, [sessionData]);

  const sessionTotals = useMemo(() => {
    const sessions = sessionData?.entities ?? [];

    return sessions.reduce(
      (total, session) => ({
        recordCount: total.recordCount + 1,
        energyKwh: total.energyKwh + (Number(session.metrics?.energyKwh) || 0),
        durationSeconds: total.durationSeconds + (Number(session.metrics?.durationSeconds) || 0),
      }),
      { recordCount: 0, energyKwh: 0, durationSeconds: 0 }
    );
  }, [sessionData]);

  const handleOpenEdit = () => {
    if (!vehicleIdNumber) return;
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    fetchData(vehicle_id);
  };

  const renderStatusBadge = () => {
    switch (vehicle?.status) {
      case VehicleStatus.APPROVED:
        return <Badge tone="success">Đã duyệt</Badge>;
      case VehicleStatus.WHITELIST:
        return <Badge tone="info">Whitelist</Badge>;
      case VehicleStatus.BANNED:
        return <Badge tone="critical">Bị cấm</Badge>;
      case VehicleStatus.UNAPPROVED:
        return <Badge tone="warning">Chưa duyệt</Badge>;
      default:
        return <Badge tone="new">-</Badge>;
    }
  };

  if (isPending && !vehicleData) {
    return <SkeletonLoading />;
  }

  return (
    <Page
      title="Thông tin xe"
      primaryAction={{
        content: 'Sửa thông tin',
        onAction: handleOpenEdit,
        disabled: !vehicleIdNumber,
      }}
      backAction={{
        onAction: () => {
          history('/vehicle_management');
        },
        content: 'Quay lại danh sách',
      }}
    >
      <Layout>
        <Layout.Section>
          <Card padding="400">
            <BlockStack gap="400">
              <InlineGrid gap="400" columns={{ xs: 1, sm: 1, md: ['oneHalf', 'oneHalf'], lg: ['oneHalf', 'oneHalf'] }}>
                <Box>
                  <Player autoplay loop={true} renderer="svg" src={evCharging} />
                </Box>
                <BlockStack gap="300">
                  <DescriptionList
                    gap="tight"
                    items={[
                      { term: 'Tag ID', description: vehicle?.tagId ?? '-' },
                      { term: 'Biển số', description: vehicle?.licensePlate ?? '-' },
                      { term: 'Hãng', description: vehicle?.brand ?? '-' },
                      { term: 'Loại xe', description: vehicle?.vehicleType ?? '-' },
                      { term: 'Năm sản xuất', description: vehicle?.manufactureYear ?? '-' },
                      { term: 'Trạng thái', description: renderStatusBadge() },
                    ]}
                  />
                </BlockStack>
              </InlineGrid>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Thống kê hôm nay
            </Text>
            <InlineGrid gap="400" columns={{ xs: 1, sm: 1, md: 3 }}>
              <Box minWidth="200px">
                <Card padding="300">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                      <Icon source={ChartLineIcon} tone="subdued" />
                      <Text as="span" tone="subdued">
                        Tổng điện năng sạc
                      </Text>
                    </InlineStack>
                    <Text variant="headingLg" as="p">
                      {helper_fmtNumber(todayAnalytics?.totalEnergyKwh ?? 0)} kWh
                    </Text>
                  </BlockStack>
                </Card>
              </Box>
              <Box minWidth="200px">
                <Card padding="300">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                      <Icon source={ClockIcon} tone="subdued" />
                      <Text as="span" tone="subdued">
                        Tổng thời gian sạc
                      </Text>
                    </InlineStack>
                    <Text variant="headingLg" as="p">
                      {helper_fmtNumber(todayAnalytics?.totalDurationMinutes ?? 0)} phút
                    </Text>
                  </BlockStack>
                </Card>
              </Box>
              <Box minWidth="200px">
                <Card padding="300">
                  <BlockStack gap="100">
                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                      <Icon source={OrderIcon} tone="subdued" />
                      <Text as="span" tone="subdued">
                        Tổng phiên sạc
                      </Text>
                    </InlineStack>
                    <Text variant="headingLg" as="p">
                      {todayAnalytics?.totalTransactions ?? 0} phiên
                    </Text>
                  </BlockStack>
                </Card>
              </Box>
            </InlineGrid>
          </BlockStack>
        </Layout.Section>

        <Layout.Section>
          <Card padding="0">
            <BlockStack gap="400">
              <Box padding="400">
                <Text as="h2" variant="headingMd">
                  Phiên sạc gần đây
                </Text>
              </Box>
              {isLoadingSessions ? (
                <Box padding="400">
                  <Text as="span" tone="subdued">
                    Đang tải dữ liệu...
                  </Text>
                </Box>
              ) : sessionRows.length > 0 ? (
                <>
                  <DataTable
                    hoverable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['Mã phiên', 'SoC', 'Năng lượng (kWh)', 'Thời gian sạc', 'Bắt đầu', 'Kết thúc', 'Trạng thái']}
                    rows={sessionRows}
                    totals={[
                      'Tổng',
                      `${sessionTotals.recordCount} phiên`,
                      `${helper_fmtNumber(sessionTotals.energyKwh)} kWh`,
                      helper_fmtTime(sessionTotals.durationSeconds),
                      '',
                      '',
                      '',
                    ]}
                    totalsName={{ singular: 'Tổng', plural: 'Tổng' }}
                    showTotalsInFooter
                    hideScrollIndicator
                  />
                  <Pagination
                    TotalRecord={sessionData?.totalItems ?? 0}
                    onChange={onChangeSessionPagination}
                    pageSize={sessionQuery.limit}
                    activeCurrentPage={sessionQuery.page}
                  />
                </>
              ) : (
                <EmptyState heading="Chưa có phiên sạc!" image={emptyIMG}>
                  <p>Hiện chưa có dữ liệu phiên sạc cho xe này.</p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
          <br />
          <br />
          <br />
          <br />
        </Layout.Section>
      </Layout>
      <VehicleModal onClose={handleCloseModal} show={isModalOpen} currentVehicleId={vehicleIdNumber} />
    </Page>
  );
}

